import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  Clock,
  Zap,
  Play,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Layers,
  TrendingUp,
  BarChart,
  Sparkles,
  FileText,
  Search
} from 'lucide-react';

const TimelineDemo = ({ data }) => {
  const [showBatching, setShowBatching] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => setShowBatching(true), 800);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  // Animation for tasks flying into batches
  const TaskCard = ({ task, index, batched }) => {
    const getTargetPosition = () => {
      if (!batched) return { x: 0, y: 0 };

      // Determine which batch this task belongs to
      const batchIndex = Math.floor(index / 3); // 3 tasks per batch for demo
      return {
        x: batchIndex * 150,
        y: Math.floor(batchIndex / 2) * 100
      };
    };

    const position = getTargetPosition();

    return (
      <div
        className={`
          absolute bg-white rounded-lg shadow-md border border-slate-200 p-3
          transition-all duration-1000 cursor-pointer hover:shadow-lg
          ${batched ? 'scale-90' : 'scale-100'}
        `}
        style={{
          left: batched ? `${20 + position.x}px` : `${(index % 4) * 140}px`,
          top: batched ? `${20 + position.y}px` : `${Math.floor(index / 4) * 80}px`,
          transform: batched ? `rotate(${index % 3 - 1}deg)` : 'rotate(0deg)',
          transitionDelay: `${index * 50}ms`
        }}
      >
        <div className="flex items-start gap-2">
          {task.type === 'essay' && <FileText className="w-4 h-4 text-azure-500 mt-0.5" />}
          {task.type === 'admin' && <Clock className="w-4 h-4 text-emerald-500 mt-0.5" />}
          {task.type === 'research' && <Search className="w-4 h-4 text-violet-500 mt-0.5" />}
          <div className="flex-1">
            <div className="text-xs font-medium text-slate-900">{task.title}</div>
            <div className="text-xs text-slate-500 mt-1">{task.hours}h • {task.college}</div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-azure-50 rounded-full mb-4">
          <Calendar className="w-4 h-4 text-azure-600" />
          <span className="text-sm font-medium text-azure-700">
            Strategic Task Management
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Intelligent Task Sequencing & Batching
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Our system analyzes cognitive load patterns and task dependencies to create
          optimal work sessions that respect both deadlines and mental energy.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Before State */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 min-h-[400px]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                {showBatching ? 'After: Smart Sessions' : 'Before: Scattered Tasks'}
                {showBatching && <Sparkles className="w-4 h-4 text-azure-600" />}
              </h3>
              <button
                onClick={() => setShowBatching(!showBatching)}
                className="px-3 py-1 bg-azure-600 text-white text-sm rounded-lg hover:bg-azure-700 transition-colors"
              >
                {showBatching ? 'Show Original' : 'Apply AI Batching'}
              </button>
            </div>

            <div className="relative h-[320px]">
              {/* Task cards */}
              {!showBatching && data.tasks.raw.slice(0, 12).map((task, index) => (
                <TaskCard key={task.id} task={task} index={index} batched={false} />
              ))}

              {/* Batched view */}
              {showBatching && (
                <div className="grid grid-cols-2 gap-4">
                  {data.tasks.batched.map((batch, batchIndex) => (
                    <div
                      key={batch.id}
                      className={`
                        bg-gradient-to-br from-azure-50 to-indigo-50 rounded-xl p-4
                        border-2 border-azure-200 cursor-pointer transition-all
                        ${selectedBatch === batch.id ? 'ring-2 ring-azure-500 shadow-lg' : 'hover:shadow-md'}
                      `}
                      onClick={() => setSelectedBatch(batch.id)}
                      style={{
                        animationDelay: `${batchIndex * 200}ms`,
                        animation: 'fadeInScale 0.6s ease-out forwards'
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {batch.type === 'focus_session' && <Zap className="w-5 h-5 text-yellow-500" />}
                          {batch.type === 'quick_wins' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                          {batch.type === 'research_session' && <BarChart className="w-5 h-5 text-violet-500" />}
                          <h4 className="font-semibold text-slate-900">{batch.title}</h4>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">{batch.tasks.length} tasks</span>
                          <span className="font-medium text-slate-900">
                            {batch.optimizedHours}h
                            <span className="text-emerald-600 ml-1">
                              (-{batch.totalHours - batch.optimizedHours}h)
                            </span>
                          </span>
                        </div>

                        <div className="text-xs text-azure-700 bg-azure-100 rounded px-2 py-1">
                          💡 {batch.insight}
                        </div>

                        <div className="flex items-center gap-2 mt-3">
                          <button className="flex-1 px-3 py-1.5 bg-white text-azure-600 border border-azure-200 rounded-lg text-sm font-medium hover:bg-azure-50 transition-colors">
                            Schedule
                          </button>
                          <button className="px-3 py-1.5 bg-azure-600 text-white rounded-lg text-sm font-medium hover:bg-azure-700 transition-colors flex items-center gap-1">
                            <Play className="w-3 h-3" />
                            Start
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats bar */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">
                    {showBatching ? '8' : '47'}
                  </div>
                  <div className="text-xs text-slate-600">
                    {showBatching ? 'Smart Sessions' : 'Individual Tasks'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">
                    {showBatching ? '65h' : '89h'}
                  </div>
                  <div className="text-xs text-slate-600">Total Time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-600">
                    {showBatching ? '24h' : '0h'}
                  </div>
                  <div className="text-xs text-slate-600">Hours Saved</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Benefits & Insights */}
        <div className="space-y-4">
          {/* Today's Focus */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Today's Focus</h3>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-900">Quick Wins Session</span>
                  <span className="text-xs text-emerald-700 font-medium">45 min</span>
                </div>
                <div className="text-xs text-slate-600">
                  6 admin tasks batched together - get them all done at once
                </div>
              </div>

              <div className="text-sm text-slate-700">
                <div className="font-medium mb-1">Why this first?</div>
                <div className="text-xs text-slate-600">
                  These quick tasks will give you momentum and clear mental space for tomorrow's essay writing session.
                </div>
              </div>
            </div>
          </div>

          {/* Smart Insights */}
          <div className="bg-gradient-to-br from-violet-50 to-azure-50 rounded-xl border border-violet-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-violet-600" />
              <h3 className="font-semibold text-slate-900">AI Insights</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">1</span>
                </div>
                <div className="text-sm text-slate-700">
                  <span className="font-medium">Cognitive Matching:</span> Essay tasks grouped by similar thinking mode
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">2</span>
                </div>
                <div className="text-sm text-slate-700">
                  <span className="font-medium">Energy Optimization:</span> Heavy tasks scheduled for your peak hours
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">3</span>
                </div>
                <div className="text-sm text-slate-700">
                  <span className="font-medium">Dependency Aware:</span> Prerequisites automatically scheduled first
                </div>
              </div>
            </div>
          </div>

          {/* Stress Reduction */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Stress Level</span>
              <span className="text-sm font-bold text-emerald-600">
                {showBatching ? '34%' : '87%'}
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-1000 ${
                  showBatching
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                    : 'bg-gradient-to-r from-red-500 to-red-600'
                }`}
                style={{ width: showBatching ? '34%' : '87%' }}
              />
            </div>
            <div className="mt-3 text-xs text-slate-600">
              {showBatching
                ? "Everything organized and manageable"
                : "Feeling overwhelmed by scattered tasks"}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default TimelineDemo;